import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { request } from '../../Utils/request';
import { Box, CircularProgress, List, ListItem, ListItemAvatar, ListItemButton, Avatar, ListItemText, Typography, Button, Chip } from '@mui/material';
import { PersonAdd, ChevronRight } from '@mui/icons-material';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';

interface Follower {
  _id: string;
  firstName: string;
  lastName: string;
  username: string;
  profilePhoto?: {
    fileKey: string;
    fileType: string;
    featured: boolean;
    url: string;
  };
  type: string;
}

interface FollowRequest {
  _id: string;
  from: {
    _id: string;
    firstName: string;
    lastName: string;
    username: string;
    profilePhoto?: {
      fileKey: string;
      fileType: string;
      featured: boolean;
      url: string;
    };
    type: string;
  };
  createdAt?: string;
}

const Followers: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [followers, setFollowers] = useState<Follower[]>([]);
  const [followRequests, setFollowRequests] = useState<FollowRequest[]>([]);
  const [processingRequest, setProcessingRequest] = useState<string | null>(null);
  const location = useLocation();

  // Determine list type based on URL (ends with /following)
  const type = location.pathname.endsWith('/following') ? 'following' : 'followers';
  const isFollowRequests = location.pathname.includes('/follow-requests');

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      try {
        // Always fetch both follow requests and followers/following
        const [followRequestsRes, followersRes] = await Promise.all([
          request(`/profile/follow-requests/incoming`, {
            method: 'GET',
            authorization: true,
          }) as any,
          request(`/profile/${id}/${type}`, {
            method: 'GET',
            authorization: true,
          }) as any
        ]);

        // Set follow requests
        const requests: FollowRequest[] = followRequestsRes?.data?.requests || followRequestsRes?.data || [];
        setFollowRequests(requests);

        // Set followers/following
        const users: Follower[] = followersRes?.data?.users || followersRes?.data || [];
        setFollowers(users);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, type]);

  const handleAcceptRequest = async (requestId: string) => {
    setProcessingRequest(requestId);
    try {
      await request(`/profile/follow-requests/${requestId}/accept`, {
        method: 'POST',
        authorization: true,
      });
      
      // Remove the accepted request from the list
      setFollowRequests(prev => prev.filter(req => req._id !== requestId));
    } catch (error) {
      console.error('Error accepting follow request:', error);
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleRejectRequest = async (requestId: string) => {
    setProcessingRequest(requestId);
    try {
      await request(`/profile/follow-requests/${requestId}/reject`, {
        method: 'POST',
        authorization: true,
      });
      
      // Remove the rejected request from the list
      setFollowRequests(prev => prev.filter(req => req._id !== requestId));
    } catch (error) {
      console.error('Error rejecting follow request:', error);
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleUnfollowUser = async (userId: string) => {
    try {
      await request(`/profile/${userId}/follow`, {
        method: 'POST',
        authorization: true,
      });
      
      // Remove the user from the list
      setFollowers(prev => prev.filter(follower => follower._id !== userId));
    } catch (error) {
      console.error('Error unfollowing user:', error);
    }
  };

  const handleFollowRequestsClick = () => {
    if (id) {
      navigate(`/profile/${id}/followers-screen`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const getTitle = () => {
    return 'Following';
  };


  const renderFollowers = () => (
    <Box>
      <Typography variant="h6" sx={{ px: 2, py: 1, fontWeight: 'bold', color: '#000' }}>
        {type === 'following' ? 'Following' : 'Followers'}
      </Typography>
      <List sx={{ px: 2 }}>
        {followers.map((user) => (
          <ListItem 
            key={user._id} 
            sx={{ 
              backgroundColor: 'white', 
              mb: 1, 
              borderRadius: "16px", 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              px: 2,
              py: 1
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <ListItemAvatar>
                <Avatar sx={{ width: 50, height: 50 }} src={user.profilePhoto?.url}>
                  {user.username?.charAt(0).toUpperCase()}
                </Avatar>
              </ListItemAvatar>
              <Box sx={{ ml: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#000' }}>
                  {user.username}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  {user.firstName} {user.lastName}
                </Typography>
              </Box>
            </Box>
            <Button 
              variant="outlined"
              onClick={() => handleUnfollowUser(user._id)}
              sx={{
                borderColor: '#000',
                color: '#000',
                borderRadius: '20px',
                px: 2,
                py: 0.5,
                fontSize: '12px',
                fontWeight: 'bold',
                textTransform: 'none',
                minWidth: 80,
                '&:hover': {
                  borderColor: '#333',
                  backgroundColor: '#f5f5f5',
                }
              }}
            >
              {type === 'following' ? 'Unfollow' : 'Remove'}
            </Button>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ width: '100%', height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f0f0f0' }}>
      <TopNavBar canGoBack title={getTitle()} />

      <Box sx={{ overflow: 'auto', py: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {followRequests.length > 0 && (
          <Box 
            onClick={handleFollowRequestsClick}
            sx={{
              backgroundColor: '#FFE4D6',
              border: '1px solid #FF6B35',
              borderRadius: '12px',
              width: '90%',
              p: 2,
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonAdd sx={{ color: '#FF6B35', mr: 2, fontSize: 24 }} />
              <Typography sx={{ color: '#FF6B35', fontWeight: 'medium' }}>
                {followRequests.length} follow request{followRequests.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
            <ChevronRight sx={{ color: '#FF6B35' }} />
          </Box>
        )}
      </Box>
        {/* {renderFollowRequests()} */}
        {renderFollowers()}
    </Box>
  );
};

export default Followers; 